import {convertPrereleaseConfiguration} from './schema-v1.js';

export const VARIANT_HEALTH_QUERY = `query ComponentHealth($ids: [ID!]!) {
  nodes(ids: $ids) { id __typename }
}`;

export function healthConfiguration(value) {
  if (!value) return null;
  const config = convertPrereleaseConfiguration(value);
  if (!Array.isArray(config.customOptions)) throw new Error('Invalid custom options.');
  for (const group of config.customOptions) {
    if (!Array.isArray(group.choices)) throw new Error('Invalid choices.');
    for (const choice of group.choices) {
      if (!Array.isArray(choice.components)) throw new Error('Invalid components.');
      for (const component of choice.components) {
        if (!/^gid:\/\/shopify\/ProductVariant\/\d+$/.test(component.variantId)) throw new Error('Invalid component variant ID.');
      }
    }
  }
  return config;
}

export async function checkConfigurations(values, query) {
  const configs = values.map(healthConfiguration);
  const ids = [...new Set(configs.flatMap(config => (config?.customOptions || []).flatMap(group => group.choices.flatMap(choice => choice.components.map(c => c.variantId)))))];
  const existing = new Set();
  for (let offset = 0; offset < ids.length; offset += 100) {
    const batch = ids.slice(offset, offset + 100);
    const result = await query(VARIANT_HEALTH_QUERY, {ids: batch});
    if (result.errors?.length || !Array.isArray(result.data?.nodes) || result.data.nodes.length !== batch.length) throw new Error('Component check failed; incomplete Shopify response.');
    result.data.nodes.forEach((node, index) => {
      if (node === null) return;
      if (node?.id !== batch[index] || node.__typename !== 'ProductVariant') throw new Error('Component check failed; unexpected Shopify response.');
      existing.add(node.id);
    });
  }
  const checkedAt = new Date().toISOString();
  return configs.map(config => {
    const issues = [];
    for (const group of config?.customOptions || []) {
      if (group.status === 'draft') continue;
      const broken = group.choices.filter(choice => choice.components.some(c => !existing.has(c.variantId)));
      const requiredBlocked = group.choices.length - broken.length < Number(group.minSelections || 0);
      for (const choice of broken) for (const component of choice.components) {
        if (existing.has(component.variantId)) continue;
        issues.push({key: `${group.id}/${choice.id}/${component.variantId}`, groupId: group.id, groupLabel: group.label, choiceId: choice.id, choiceLabel: choice.label, variantId: component.variantId, requiredBlocked, conditional: Boolean(group.conditions?.rules?.length)});
      }
    }
    return {status: issues.length ? 'missing' : 'healthy', checkedAt, issues};
  });
}

export function healthLabel(health) {
  if (!health) return 'Not checked';
  if (health.status === 'error') return 'Check failed';
  if (health.issues.some(issue => issue.requiredBlocked)) return 'Required option affected';
  return health.issues.length ? 'Missing component' : 'Healthy';
}

export function combinedHealth(reports) {
  const issues = new Map();
  const failedVersions = [];
  reports.forEach((report, index) => {
    const version = index ? 'Draft' : 'Published';
    if (report.status === 'error') failedVersions.push(version);
    for (const issue of report.issues || []) {
      const previous = issues.get(issue.key);
      issues.set(issue.key, {
        ...issue,
        requiredBlocked: Boolean(previous?.requiredBlocked || issue.requiredBlocked),
        conditional: previous ? previous.conditional && issue.conditional : issue.conditional,
        versions: [...new Set([...(previous?.versions || []), version])],
      });
    }
  });
  return {issues: [...issues.values()].map(issue => ({...issue, affected: issue.versions.length === 2 ? 'Both' : issue.versions[0]})), failedVersions};
}
